import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import * as queries from '../../../graphql/queries';
import specialtiesData from '../../../data/SpecialtyOverview.json';
import { fetchUserQuizResults } from '../../../api/dreamSpecialtyApi';
import { updateUserSpecialty } from '../../../api/userProfileApi';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import './DreamSpecialtyStyles.css';
import { FaUserAstronaut, FaBriefcaseMedical } from 'react-icons/fa';
import '../../../pages/QuizChoice.css';
import { saveMBTIResults } from '../../../api/mbtiQuizApi';
import { saveMedicalQuizResults } from '../../../api/medicalQuizApi';
import OnboardingProgressBar from '../../common/OnboardingProgressBar';

const DreamSpecialty = () => {
  const client = generateClient();
  const selectionCardRef = useRef(null);

  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [quizData, setQuizData] = useState({ mbti: null, medical: null });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overlappingSpecialties, setOverlappingSpecialties] = useState([]);
  const [selectedFromList, setSelectedFromList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;

    const loadQuizResults = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const results = await fetchUserQuizResults();
        if (!mounted) return;

        setQuizData(results);

        if (results?.mbti && results?.medical) {
          const mbtiSpecialties = JSON.parse(results.mbti.recommendedSpecialties || '[]');
          const medicalSpecialties = JSON.parse(results.medical.bestMatches || '[]').map(s => s.name);
          const overlapping = mbtiSpecialties.filter(s => medicalSpecialties.includes(s));
          setOverlappingSpecialties(overlapping);
        }
      } catch (err) {
        if (mounted) {
          setError(err.message || 'Failed to load quiz results');
          console.error('Error loading quiz results:', err);
        }
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    loadQuizResults();
    return () => { mounted = false; };
  }, [currentUser]);

  useEffect(() => {
    const initUser = async () => {
      try {
        const user = await getCurrentUser();
        const attributes = await fetchUserAttributes();
        setCurrentUser({ ...user, attributes });
      } catch (err) {
        console.error('Error getting user:', err);
      }
    };
    initUser();
  }, []);

  useEffect(() => {
    try {
      const pending = localStorage.getItem('medmap.pendingSpecialty');
      if (pending && !selectedSpecialty) {
        setSelectedSpecialty(pending);
        setSelectedFromList(true);
      }
    } catch {}
  }, [selectedSpecialty]);

  const waitForProgressBar = async (nextStep) => {
    setOnboardingStep(nextStep);
    // OnboardingProgressBar uses ~700ms transition; add small buffer.
    await new Promise(r => setTimeout(r, 760));
  };

  const handleSpecialtyClick = (specialty) => {
    const specialtyName = typeof specialty === 'string' ? specialty : specialty.name;
    setSelectedSpecialty(specialtyName);
    setSelectedFromList(true);
    
    if (selectionCardRef.current) {
      selectionCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleCommitSpecialty = async () => {
    console.log('handleCommitSpecialty called with specialty:', selectedSpecialty);
    try {
      setSaving(true);
      setSaveError(null);
      setIsAdvancing(true);

      if (!currentUser) {
        try {
          localStorage.setItem('medmap.pendingSpecialty', selectedSpecialty || '');
        } catch {}
        await waitForProgressBar(2);
        navigate('/login', { state: { from: '/account_create' } });
        return;
      }

      try {
        const attributes = currentUser?.attributes || (await fetchUserAttributes());
        const userId = attributes?.sub;
        const username = currentUser?.username;

        const mbtiRaw = localStorage.getItem('medmap.mbti.result');
        if (mbtiRaw) {
          const mbti = JSON.parse(mbtiRaw);
          await saveMBTIResults(
            mbti.personalityType,
            mbti.recommendedSpecialties || [],
            mbti.scores || {},
            username
          );
        }

        const medRaw = localStorage.getItem('medmap.medical.result');
        if (medRaw && userId && username) {
          const med = JSON.parse(medRaw);
          await saveMedicalQuizResults(
            userId,
            med.bestMatches || [],
            med.worstMatches || [],
            username,
            med.selectedAnswers || []
          );
        }
      } catch (err) {
        console.warn('Saving cached quiz results failed or were missing:', err);
      }

      const { data } = await client.graphql({
        query: queries.listUserProfiles,
        variables: {
          filter: { owner: { eq: currentUser.username } }
        },
        authMode: 'userPool'
      });

      if (!data.listUserProfiles.items.length) {
        try { localStorage.setItem('medmap.pendingSpecialty', selectedSpecialty || ''); } catch {}
        await waitForProgressBar(2);
        navigate('/account_create', { state: { from: '/dream-specialty' }, replace: true });
        return;
      }

      const userProfile = data.listUserProfiles.items[0];

      const result = await updateUserSpecialty(userProfile.id, selectedSpecialty);
      console.log('Save result:', result);

      try { localStorage.removeItem('medmap.pendingSpecialty'); } catch {}

      await waitForProgressBar(3);
      navigate('/pathfinder-setup', {
        state: {
          selectedSpecialty,
          quizData,
          isGoal: true
        }
      });
    } catch (error) {
      console.error('Save error:', error);
      setSaveError(error.message);
    } finally {
      setSaving(false);
      setIsAdvancing(false);
    }
  };

  // Retake handlers
  const handleRetakeMbti = () => {
    // Clear MBTI quiz progress so it starts fresh
    try {
      localStorage.removeItem('currentQuestion');
      localStorage.removeItem('scores');
      localStorage.removeItem('selectedAnswers');
      localStorage.removeItem('answeredQuestions');
    } catch {}
    navigate('/quiz?retake=1');
  };

  const handleRetakeWorkstyle = () => {
    // Optionally clear any prior medical quiz local storage keys if used in future
    // localStorage.removeItem('medicalSelectedAnswers');
    navigate('/medmatch?retake=1');
  };

  const renderSpecialtyItem = (specialty, source) => {
    const isOverlapping = overlappingSpecialties.includes(
      typeof specialty === 'string' ? specialty : specialty.name
    );
    const isSelected = selectedSpecialty === (typeof specialty === 'string' ? specialty : specialty.name);
    
    return (
      <div 
        className={`specialty-item ${isOverlapping ? 'overlapping' : ''} ${isSelected ? 'selected' : ''}`}
        onClick={() => handleSpecialtyClick(specialty)}
        role="button"
        tabIndex={0}
      >
        {typeof specialty === 'string' ? (
          specialty
        ) : (
          <>
            {specialty.name}
            <span className="score-badge">Score: {specialty.score}</span>
          </>
        )}
        {isOverlapping && (
          <span className="score-badge">Recommended by both quizzes!</span>
        )}
      </div>
    );
  };

  // Loading state (home-aligned)
  if (isLoading) {
    return (
      <section className="ds-root">
        <div className="ds-inner">
          <OnboardingProgressBar currentStep={1} />
          <header className="ds-header">
            <h1 className="ds-title">
              Know your <span className="ds-gradient-text">Dream Specialty?</span>
            </h1>
            <p className="ds-subtitle">We’re loading your recommendations…</p>
          </header>
          <div className="ds-loading">
            <div className="ds-spinner" aria-label="Loading" />
          </div>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="ds-root">
        <div className="ds-inner">
          <OnboardingProgressBar currentStep={1} />
          <header className="ds-header">
            <h1 className="ds-title">
              Know Your <span className="ds-gradient-text">Dream Specialty?</span>
            </h1>
            <p className="ds-subtitle">There was a problem loading your results.</p>
          </header>
          <div className="ds-error">
            <h3>Something went wrong</h3>
            <p>{error}</p>
            <button className="ds-cta-secondary" onClick={() => window.location.reload()}>
              Try again
            </button>
          </div>
        </div>
      </section>
    );
  }

  const hasMbtiResults = quizData.mbti !== null;
  const hasMedicalResults = quizData.medical !== null;

  return (
    <section className="ds-root">
      <div className="ds-inner">
        <OnboardingProgressBar currentStep={onboardingStep} />
        {/* Header */}
        <header className="ds-header">
          <h1 className="ds-title">
            Know Your <span className="ds-gradient-text">Dream Specialty?</span>
          </h1>
          <p className="ds-subtitle">
            Choose your specialty from the menu below to continue. Or complete a quiz to discover the right fit for you.
          </p>
        </header>

        {/* Selection Card */}
        <div className="ds-card ds-selection-card" ref={selectionCardRef}>
          <div className="ds-selection-head">
            <h3 className="ds-card-title">Choose Your Dream Specialty</h3>
            <p className="ds-card-text">
              Select a specialty directly, or complete a quiz below.
            </p>
          </div>

          {selectedFromList ? (
            <div className="ds-selected-pill">
              You’ve selected <strong>{selectedSpecialty}</strong>
              <button className="ds-link-btn" onClick={() => setSelectedFromList(false)}>
                Change selection
              </button>
            </div>
          ) : (
            <div className="ds-select-row">
              <label htmlFor="ds-select" className="sr-only"></label>
              <select
                id="ds-select"
                className="ds-select"
                value={selectedSpecialty}
                onChange={(e) => setSelectedSpecialty(e.target.value)}
              >
                <option value="">Select a specialty…</option>
                {specialtiesData.specialties.map((s) => (
                  <option key={s.id} value={s.name}>{s.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="ds-actions">
            <button
              className="ds-cta-primary"
              onClick={handleCommitSpecialty}
              disabled={!selectedSpecialty || saving || isAdvancing}
            >
              {saving || isAdvancing ? 'Saving…' : 'Lock in this goal and continue to Pathfinder'}
            </button>
            {saveError && <div className="ds-inline-error">{saveError}</div>}
          </div>
        </div>

        {/* NEW: minimal quiz hint below selection card */}
        <div className="ds-quiz-hint" aria-label="Quiz suggestion">
          <p className="ds-quiz-hint-text" style={{ fontSize: '1.05rem' }}>
            Don’t know yet? Take a{' '}
            <span className="ds-gradient-text" style={{ fontWeight: 700 }}>
              Quiz
            </span>{' '}
            to find out.
          </p>
          <div className="ds-quiz-hint-arrows" aria-hidden="true">
            <span className="ds-arrow left" style={{ fontSize: '1.45rem' }}>↓</span>
            <span className="ds-arrow right" style={{ fontSize: '1.45rem' }}>↓</span>
          </div>
        </div>

        {/* Results Grid */}
        <div className="ds-grid">
          {/* MBTI side: show results if present, else show CTA panel */}
          {hasMbtiResults ? (
            <article className="ds-card">
              <header className="ds-card-header">
                <div className="ds-card-icon">{/* decorative */}</div>
                <div>
                  <h3 className="ds-card-eyebrow">MBTI Personality Insights</h3>
                  <div className="ds-card-sub">Type: {quizData.mbti.personalityType}</div>
                </div>
              </header>

              <div className="ds-list">
                <h4 className="ds-list-title">Recommended Specialties</h4>
                <div className="ds-items">
                  {JSON.parse(quizData.mbti.recommendedSpecialties).map((specialty) =>
                    renderSpecialtyItem(specialty, 'mbti')
                  )}
                </div>
              </div>

              {/* Retake MBTI button */}
              <div className="ds-actions" style={{ justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button className="ds-cta-secondary" onClick={handleRetakeMbti}>
                  Retake MBTI Quiz
                </button>
              </div>
            </article>
          ) : (
            <Link
              to="/quiz"
              className="qc-card"
              aria-label="MBTI Quiz – discover personality type and matching specialties"
            >
              <div className="qc-card-icon">
                <FaUserAstronaut />
              </div>
              <h2 className="qc-card-title">MBTI Personality Quiz</h2>
              <p className="qc-card-text">
                Uncover your cognitive preference profile and see which specialties align with your interpersonal & decision styles.
              </p>

              {/* NEW: estimated time */}
              <p style={{ margin: '0 0 .85rem', fontSize: '.9rem', color: '#64748b' }}>
                Estimated completion time:{' '}
                <span className="ds-gradient-text" style={{ fontWeight: 700 }}>
                  10 minutes
                </span>
              </p>

              <div className="qc-card-footer">
                <span className="qc-card-action">Start MBTI Quiz</span>
                <span className="qc-card-arrow" aria-hidden="true">→</span>
              </div>
            </Link>
          )}

          {/* Workstyle side: show results if present, else show CTA panel */}
          {hasMedicalResults ? (
            <article className="ds-card">
              <header className="ds-card-header">
                <div className="ds-card-icon alt" />
                <div>
                  <h3 className="ds-card-eyebrow">Workstyle Alignment</h3>
                  <div className="ds-card-sub">Best Matches</div>
                </div>
              </header>

              <div className="ds-list">
                <h4 className="ds-list-title">Top Matches</h4>
                <div className="ds-items">
                  {JSON.parse(quizData.medical.bestMatches).map((specialty) =>
                    renderSpecialtyItem(specialty, 'medical')
                  )}
                </div>
              </div>

              {/* Retake Workstyle button */}
              <div className="ds-actions" style={{ justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button className="ds-cta-secondary" onClick={handleRetakeWorkstyle}>
                  Retake Workstyle Quiz
                </button>
              </div>
            </article>
          ) : (
            <Link
              to="/medmatch"
              className="qc-card"
              aria-label="Workstyle Quiz – match specialties by work preferences"
            >
              <div className="qc-card-icon variant-alt">
                <FaBriefcaseMedical />
              </div>
              <h2 className="qc-card-title">Workstyle Alignment Quiz</h2>
              <p className="qc-card-text">
                Rank specialties using lifestyle, procedural intensity, acuity mix, teamwork dynamics, and training pathway factors.
              </p>

              {/* NEW: estimated time */}
              <p style={{ margin: '0 0 .85rem', fontSize: '.9rem', color: '#64748b' }}>
                Estimated completion time:{' '}
                <span className="ds-gradient-text" style={{ fontWeight: 700 }}>
                  30 seconds
                </span>
              </p>

              <div className="qc-card-footer">
                <span className="qc-card-action">Start Workstyle Quiz</span>
                <span className="qc-card-arrow" aria-hidden="true">→</span>
              </div>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

export default DreamSpecialty;

